import {
  Box,
  Button,
  Drawer,
  ToastContainer,
  useBreakpoint,
  useOpenClose,
  useTheme,
} from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Link, NavLink, Outlet, ScrollRestoration } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import {
  DataFilterOperation,
  ItcEligibilityProgramTypes,
  ProgramType,
  QueueStatus,
  UserPermissions,
  WarrantyProvider,
} from 'types';

import './App.css';
import { useAuth } from './components/auth/authProvider';
import RequirePalmettoFinanceUser, { isPalmettoFinanceUser } from './components/auth/RequirePalmettoFinanceUser';
import RequirePermissions from './components/auth/requirePermissions';
import { useInviteToken } from './components/auth/useInviteToken';
import { DropdownMenu } from './components/DropdownMenu';
import { MaintenancePortalLink } from './components/MaintenancePortalLink';
import OrganizationMenu from './components/OrganizationMenu';
import Nav from './components/settings/nav';
import { OrganizationStatusBanner } from './components/settings/organizations/OrganizationStatusBanner';
import { UserMenu } from './components/UserMenu';
import { isNextEnvironment } from './helpers/isNextEnvironment';
import { useGetAdminMenuItems } from './hooks/useGetAdminMenuItems';
import { useGetEPCAdminMenuItems } from './hooks/useGetEPCAdminMenuItems';
import { useGetMenuItemsAccess } from './hooks/useGetMenuItemsAccess';
import { useGetQueueMenuItems } from './hooks/useGetQueueMenuItems';
import usePermissions from './hooks/usePermissions';
import { RateLimitBanner } from './RateLimitBanner';
import { ServiceUnavailableBanner } from './ServiceUnavailableBanner';

const accountMenuItems = [
  { name: 'Pipeline', path: '/accounts' },
  { name: 'Post-Activation', path: '/accounts?postActivation' },
  {
    name: 'Cancelled',
    path: '/accounts?cancelled',
    permissions: [UserPermissions.admin],
  },
];

function App() {
  useIntercom();
  const { isPhone } = useBreakpoint();
  const { isAuthenticated, claims, organizationData } = useAuth();
  const { isInviteToken } = useInviteToken();
  const userPermissions = usePermissions();
  const {
    ptoQueue = false,
    enablePayouts = false,
    feocDcCalculator = false,
    enableWarrantyRegistration = false,
    incentivesManagement = false,
    enableQcChecklists = false,
    enableLeadManagement = false,
    enableGeothermal = false,
    enableAvlAdmin = false,
    welcomeCallStipRulesEnabled = false,
    accountPromos = false,
  } = useFlags();
  const isAdmin = userPermissions.includes(UserPermissions.admin);
  const isPalmettoUser = isPalmettoFinanceUser(claims);
  const isLightReachAdmin = isPalmettoUser && isAdmin;
  const isLightReachAccountingAdmin =
    isPalmettoUser && userPermissions.includes(UserPermissions.lightReachAccountingAdmin);
  const isTaskAdmin = isPalmettoUser && userPermissions.includes(UserPermissions.lightReachTaskAdmin);
  const isOrgAccountingAdmin = userPermissions.includes(UserPermissions.orgAccountingAdmin);
  const isLightReachEPCAccountingAdmin = isPalmettoUser && isOrgAccountingAdmin;
  const isLightReachQCSettingsAdmin =
    isPalmettoUser && userPermissions.includes(UserPermissions.lightReachQCSettingsAdmin);
  const isLightReachQCTech = userPermissions.includes(UserPermissions.lightReachQCTech);
  const isLightReachSupport = userPermissions.includes(UserPermissions.lightReachSupport);
  const isLightReachAccountingReader = userPermissions.includes(UserPermissions.lightReachAccountingReader);
  const isLightReachAccountingSettings = userPermissions.includes(UserPermissions.lightReachAccountingSettings);
  const isLightReachIncentiveAdmin = userPermissions.includes(UserPermissions.lightReachIncentiveAdmin);
  const isLightReachCapitalMarketsAdmin =
    isPalmettoUser && userPermissions.includes(UserPermissions.lightReachCapitalMarketsAdmin);
  const isBillingBlockerAdmin = isPalmettoUser && userPermissions.includes(UserPermissions.billingBlockerAdmin);
  const isBulkPaymentLinkAdmin = isPalmettoUser && userPermissions.includes(UserPermissions.lightReachSupport);
  const isUnderwritingAdmin = isPalmettoUser && userPermissions.includes(UserPermissions.underwritingAdmin);
  const isPricingAdmin = isPalmettoUser && userPermissions.includes(UserPermissions.pricingAdmin);
  const isHardwareExceptionsAdmin = isPalmettoUser;
  const isHvacOrWaterHeater = (organizationData?.programTypes ?? []).some(
    (programType) => programType === ProgramType.hvac || programType === ProgramType.waterHeater,
  );
  const hasItcEligibilityProgramType = (organizationData?.programTypes ?? []).some((programType) =>
    ItcEligibilityProgramTypes.includes(programType),
  );
  const onPointIsPrimaryWarrantyProvider =
    organizationData?.extendedWarrantyDealerInformation?.some(
      (provider) => provider.warrantyProvider === WarrantyProvider.onPointWarranty && provider.isPreferredProvider,
    ) ?? false;

  const {
    adminDashboardAccess,
    epcDashboardAccess,
    adminPayoutsAccess,
    epcPayoutsAccess,
    adminTaskAccess,
    onPointClaimFormAccess,
  } = useGetMenuItemsAccess({
    isLightReachAdmin,
    isOrgAccountingAdmin,
    enablePayouts,
    isLightReachAccountingAdmin,
    isLightReachEPCAccountingAdmin,
    isLightReachAccountingReader,
    isLightReachAccountingSettings,
    isTaskAdmin,
    isHvacOrWaterHeater,
    onPointIsPrimaryWarrantyProvider,
  });

  const adminMenuItems = useGetAdminMenuItems({
    adminPayoutsAccess,
    adminDashboardAccess,
    adminTaskAccess,
    isLightReachQCSettingsAdmin,
    feocDcCalculator,
    incentivesManagement: incentivesManagement && isLightReachIncentiveAdmin,
    enableQcChecklists,
    isLightReachCapitalMarketsAdmin,
    isBillingBlockerAdmin,
    isPalmettoUser,
    isBulkPaymentLinkAdmin,
    isLightReachSupport,
    enableAvlAdmin,
    isHardwareExceptionsAdmin,
    isUnderwritingAdmin,
    welcomeCallStipRulesEnabled,
    promosEnabled: accountPromos && isPricingAdmin,
    isHarnessEnvironment: isNextEnvironment(),
    hasItcEligibilityProgramType,
  });

  const epcAdminMenuItems = useGetEPCAdminMenuItems({
    epcPayoutsAccess,
    epcDashboardAccess,
    feocDcCalculator,
    enableLeadManagement,
    onPointClaimFormAccess,
    hasItcEligibilityProgramType,
  });

  const { isOpen: isDrawerOpen, handleClose: closeDrawer, handleToggle: toggleDrawer } = useOpenClose();

  const { theme } = useTheme();

  let logoUrl = theme === 'light' ? '/palmetto-brand-logo.svg' : '/palmetto-brand-logo-dark.svg';

  if (theme === 'system') {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

    logoUrl = systemTheme === 'light' ? '/palmetto-brand-logo.svg' : '/palmetto-brand-logo-dark.svg';
  }

  const defaultQueueFilterAsReadyAndReReview = [
    {
      id: 'status',
      operation: DataFilterOperation.Equal,
      selectedValues: [QueueStatus.submitted, QueueStatus.resubmitted],
    },
  ];
  const defaultQueueFilterAsNotReady = [
    {
      id: 'status',
      operation: DataFilterOperation.NotEqual,
      selectedValues: [QueueStatus.rejected],
    },
  ];

  const queueItems = useGetQueueMenuItems({
    enableWarrantyRegistration,
    ptoQueue,
    enableGeothermal,
    isLightReachQCTech,
    isLightReachSupport,
    defaultQueueFilterAsReadyAndReReview,
    defaultQueueFilterAsNotReady,
  });
  return (
    <Box background="secondary" color="body-primary" fontSize="sm">
      <ScrollRestoration />
      <ToastContainer />
      <RateLimitBanner />
      <ServiceUnavailableBanner />
      {isPhone && !isInviteToken && (
        <Drawer isOpen={isDrawerOpen} onDismiss={closeDrawer} ariaLabel="menu" placement="left">
          <Box
            padding="4xl sm lg sm"
            fontSize="sm"
            background="secondary"
            height="100"
            childGap="sm"
            className="settings-nav"
          >
            <Box margin="lg 0 0 0" padding="0 0 0 sm" fontWeight="medium" color="contrast">
              Accounts
            </Box>

            <Box>
              {accountMenuItems?.map((item: any) => (
                <NavLink key={item.path} to={item.path} onClick={closeDrawer}>
                  {item.name}
                </NavLink>
              ))}
            </Box>

            <RequirePalmettoFinanceUser>
              <Box margin="lg 0 0 0" padding="0 0 0 sm" fontWeight="medium" color="contrast">
                Queues
              </Box>

              <Box>
                {queueItems?.map((item: any) => {
                  const searchParams = item.defaultFilters
                    ? `?advancedFilters=${encodeURIComponent(JSON.stringify(item.defaultFilters))}`
                    : '';
                  return (
                    <NavLink key={item.path} to={`${item.path}${searchParams}`} onClick={closeDrawer}>
                      {item.name}
                    </NavLink>
                  );
                })}
              </Box>
            </RequirePalmettoFinanceUser>
            <RequirePalmettoFinanceUser>
              <Box margin="lg 0 0 0" padding="0 0 0 sm" fontWeight="medium" color="contrast">
                Admin
              </Box>

              <Box>
                {adminMenuItems?.map((item: any) => (
                  <NavLink key={item.path} to={item.path} onClick={closeDrawer}>
                    {item.name}
                  </NavLink>
                ))}
              </Box>
            </RequirePalmettoFinanceUser>
            {!isLightReachAdmin && (
              <RequirePermissions
                permissions={[UserPermissions.admin, UserPermissions.orgAccountingAdmin, UserPermissions.editor]}
                checkAllPermissions={false}
              >
                {epcAdminMenuItems.length > 0 && (
                  <>
                    <Box margin="lg 0 0 0" padding="0 0 0 sm" fontWeight="medium" color="contrast">
                      Tools
                    </Box>
                    <Box>
                      {epcAdminMenuItems?.map((item: any) =>
                        item.external ? (
                          <a
                            key={item.path}
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={closeDrawer}
                          >
                            {item.name}
                          </a>
                        ) : (
                          <NavLink key={item.path} to={item.path} onClick={closeDrawer}>
                            {item.name}
                          </NavLink>
                        ),
                      )}
                    </Box>
                  </>
                )}
              </RequirePermissions>
            )}

            <Box margin="lg 0 0 0" padding="0 0 0 sm" fontWeight="medium" color="contrast">
              Settings
            </Box>
            <Nav onNavLinkClick={closeDrawer} />
          </Box>
        </Drawer>
      )}
      <Box
        as="header"
        borderWidth="0 0 xs 0"
        borderColor="separator"
        direction="row"
        justifyContent="space-between"
        fontSize={{ base: 'sm', desktop: 'md' }}
        background="primary"
      >
        <Box
          direction="row"
          childGap="md"
          alignItems="center"
          fontSize="sm"
          justifyContent="center"
          padding={{ base: 'sm md', desktop: 'md lg' }}
        >
          {isPhone && !isInviteToken ? (
            <Button
              iconPrefix="menu"
              size="sm"
              variant="tertiary"
              tone="neutral"
              onClick={toggleDrawer}
              style={{
                zIndex: isDrawerOpen ? 'var(--size-z-index-popover)' : undefined,
              }}
            />
          ) : !isInviteToken ? (
            <Link to="/">
              <img src={logoUrl} alt="Palmetto" style={{ height: isPhone ? '32px' : '42px' }} />
            </Link>
          ) : (
            <img src="/palmetto-brand-logo.svg" alt="Palmetto" style={{ height: isPhone ? '32px' : '42px' }} />
          )}
          {isAuthenticated && <OrganizationMenu />}
          {!isPhone && !isInviteToken && (
            <>
              <DropdownMenu items={accountMenuItems} title="Accounts" />
              <RequirePalmettoFinanceUser>
                <DropdownMenu items={queueItems} title="Queues" />
              </RequirePalmettoFinanceUser>
              <RequirePalmettoFinanceUser>
                <DropdownMenu items={adminMenuItems} title="Admin" />
              </RequirePalmettoFinanceUser>

              {!isLightReachAdmin && (
                <RequirePermissions
                  permissions={[UserPermissions.admin, UserPermissions.orgAccountingAdmin, UserPermissions.editor]}
                  checkAllPermissions={false}
                >
                  {epcAdminMenuItems.length > 0 && <DropdownMenu items={epcAdminMenuItems} title="Tools" />}
                </RequirePermissions>
              )}
            </>
          )}

          {!isPhone && !isInviteToken && <MaintenancePortalLink variant="topNav" />}
        </Box>
        {isAuthenticated && <UserMenu />}
      </Box>
      <Box
        margin={{ base: 'lg auto 5xl', desktop: 'xl auto 5xl' }}
        padding={{ tablet: '0 lg', desktop: '0 3xl', hd: '0 4xl' }}
        width="100"
      >
        <Box margin={{ base: '0 0 lg', desktop: '0 md md' }} padding={{ base: '0', desktop: '0 md' }}>
          <OrganizationStatusBanner />
        </Box>
        <Outlet />
      </Box>
    </Box>
  );
}

export default App;
